import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePermissions } from "@/components/auth";
import {
  listarUsuarios,
  atualizarUsuario,
  excluirUsuario,
} from "@/services/gestaoUsuariosService";
import type { ProfileForGestao, UserUpdateData } from "@/types/users";
import type { UserRole } from "@/types/permissions";
import { UsuarioLeadsGestao } from "@/components/admin/UsuarioLeadsGestao";

const ROLES: { value: UserRole; label: string }[] = [
  { value: "admin", label: "Administrador" },
  { value: "representante", label: "Representante" },
  { value: "vendedor", label: "Vendedor" },
  { value: "parceiro", label: "Parceiro" },
  { value: "financeiro", label: "Financeiro" },
  { value: "ai_agent", label: "Agente IA" },
];

function roleLabel(role: UserRole): string {
  return ROLES.find((r) => r.value === role)?.label ?? role;
}

export function GestaoUsuariosSection() {
  const permissions = usePermissions();
  const currentUserId = permissions.userProfile?.id ?? null;
  const [usuarios, setUsuarios] = useState<ProfileForGestao[]>([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<ProfileForGestao | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<UserUpdateData>({});

  const load = async () => {
    setLoading(true);
    try {
      const list = await listarUsuarios();
      setUsuarios(list);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (user: ProfileForGestao) => {
    setSelected(user);
    setForm({
      nome: user.nome,
      role: user.role,
      ativo: user.ativo,
      telefone: user.telefone ?? "",
      cidade: user.cidade ?? "",
      estado: user.estado ?? "",
      cpf: user.cpf ?? "",
      pix: user.pix ?? "",
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setSelected(null);
    setForm({});
  };

  const handleSaveEdit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await atualizarUsuario(selected.id, {
        nome: form.nome,
        role: form.role,
        ativo: form.ativo,
        telefone: form.telefone || null,
        cidade: form.cidade || null,
        estado: form.estado || null,
        cpf: form.cpf || null,
        pix: form.pix || null,
      });
      toast.success("Usuário atualizado com sucesso.");
      closeEdit();
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao atualizar usuário");
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (user: ProfileForGestao) => {
    setSelected(user);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setSelected(null);
  };

  const handleConfirmDelete = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await excluirUsuario(selected.id, selected.role);
      toast.success("Usuário excluído permanentemente. O e-mail poderá ser reutilizado em novos cadastros.");
      closeDelete();
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao excluir usuário");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Gestão de Usuários
        </CardTitle>
        <CardDescription>
          Gerencie usuários, tipo de acesso e dados cadastrais
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Total de Usuários</p>
            <p className="text-2xl font-bold">{loading ? "—" : usuarios.length}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-2">
            {usuarios.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                Nenhum usuário encontrado.
              </p>
            ) : (
              usuarios.map((user) => (
                <div
                  key={user.id}
                  className="flex flex-wrap items-center justify-between gap-2 p-3 border rounded-md"
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{user.nome}</p>
                    <p className="text-sm text-muted-foreground truncate">
                      {user.telefone || "—"} {user.cidade && user.estado ? ` • ${user.cidade}/${user.estado}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant={user.ativo ? "secondary" : "outline"}>
                      {roleLabel(user.role)}
                    </Badge>
                    {!user.ativo && (
                      <Badge variant="destructive">Inativo</Badge>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(user)}
                      aria-label={`Editar ${user.nome}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDelete(user)}
                      disabled={!user.ativo || user.id === currentUserId}
                      title={user.id === currentUserId ? "Você não pode excluir sua própria conta" : "Excluir usuário permanentemente"}
                      aria-label={`Excluir ${user.nome}`}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </CardContent>

      {/* Dialog Editar */}
      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          if (!open) closeEdit();
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar usuário</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="grid gap-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="edit-nome">Nome</Label>
                <Input
                  id="edit-nome"
                  value={form.nome ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-telefone">Telefone</Label>
                <Input
                  id="edit-telefone"
                  value={form.telefone ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-cidade">Cidade</Label>
                  <Input
                    id="edit-cidade"
                    value={form.cidade ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, cidade: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-estado">Estado</Label>
                  <Input
                    id="edit-estado"
                    value={form.estado ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, estado: e.target.value }))}
                    placeholder="UF"
                    maxLength={2}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-cpf">CPF</Label>
                <Input
                  id="edit-cpf"
                  value={form.cpf ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, cpf: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-pix">PIX</Label>
                <Input
                  id="edit-pix"
                  value={form.pix ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, pix: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo de acesso</Label>
                <Select
                  value={form.role ?? selected.role}
                  onValueChange={(value: UserRole) => setForm((f) => ({ ...f, role: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-ativo"
                  checked={form.ativo ?? selected.ativo}
                  onCheckedChange={(checked) => setForm((f) => ({ ...f, ativo: checked }))}
                />
                <Label htmlFor="edit-ativo">Usuário ativo</Label>
              </div>

              <UsuarioLeadsGestao
                userId={selected.id}
                userRole={selected.role}
                usuarios={usuarios}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeEdit}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {saving ? " Salvando…" : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog Excluir */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário permanentemente</AlertDialogTitle>
            <AlertDialogDescription>
              O usuário <strong>{selected?.nome}</strong> será excluído do sistema. Todos os registros (perfil, autenticação) serão removidos e o e-mail ficará livre para novos cadastros. Esta ação não pode ser desfeita. Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeDelete}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={saving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {saving ? " Excluindo…" : "Excluir permanentemente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
